import React, { useCallback, useState, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import {
  Card,
  Flex,
  LoadingIndicator,
  SlideoutDrawerV2,
  Tab,
  Tabs,
  Text,
  Link,
  toast,
  Button,
  ModalV2,
  HelpIndicator,
  Textarea,
} from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import {
  useGetPayeeQuery,
  useApprovePayeeMutation,
  useReturnPayeeMutation,
  useGetPayeeLogsLazyQuery,
  PayeeStatus as PayeeStatusType,
} from "../../../../graphql/supergraph.index";
import { PayeeType, PayeeStatus } from "../../../../graphql/supergraphOperations";
import { clientContext } from "../../../../utils/graphql_utils";
import { PayeeWorkflowModes } from "../../Context/constants";
import { PayeeData } from "../../Context/PayeeContext";
import { PayeeEntityWorkflow } from "../../PayeeEntityWorkflow";
import { PayeeInvestmentWorkflow } from "../../PayeeInvestmentWorkflow";
import { PayeeVendorWorkflow } from "../../PayeeVendorWorkflow";
import { DocumentTracker } from "../DocumentTracker";
import { PayeePaymentPrefDisplayCard } from "../PayeePaymentPrefDisplayCard";
import { PaymentDetailField } from "../PaymentDetailField";
import { PayeeLogsList } from "./PayeeLogsList";
import {
  slideoutContainer,
  leftColumn,
  rightColumn,
  rightColumnContent,
  rightColumnHeader,
  rightColumnTabsContainer,
  actionButtonsContainer,
  returnReasonModalContent,
  complexHeaderOverride,
} from "./styles_vanilla.css";
import { PayeeDetailsProps, PayeeDetailsTabs } from "./types";
import { transformPayeeDataForEdit } from "./utils";

export const PayeeDetails: React.FC<PayeeDetailsProps> = ({ isOpen, onClose, payeeId }) => {
  const apolloClient = useApolloClient();
  const [activeTab, setActiveTab] = useState<PayeeDetailsTabs>(PayeeDetailsTabs.Overview);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState("");

  // Reset state when payeeId changes to prevent stale data and tab state
  useEffect(() => {
    if (payeeId) {
      setActiveTab(PayeeDetailsTabs.Overview);
      setShowReturnModal(false);
      setReturnReason("");

      // Clear all cached logs data to prevent stale data from previous payees
      apolloClient.cache.evict({
        fieldName: "payeeLogs",
      });
      apolloClient.cache.gc();
    }
  }, [payeeId, apolloClient]);

  const { data, loading, error } = useGetPayeeQuery({
    variables: {
      payeeId: payeeId!,
    },
    skip: !payeeId,
    fetchPolicy: "cache-and-network",
    context: clientContext(),
  });

  const [fetchLogs, { data: logsData, loading: logsLoading, error: logsError }] =
    useGetPayeeLogsLazyQuery({
      fetchPolicy: "cache-and-network",
      context: clientContext(),
    });

  const handleTabChange = useCallback(
    (tab: PayeeDetailsTabs) => {
      setActiveTab(tab);
      // Fetch logs when Log tab is selected
      if (tab === PayeeDetailsTabs.Log && payeeId) {
        void fetchLogs({ variables: { payeeId } });
      }
    },
    [payeeId, fetchLogs, setActiveTab],
  );

  const payee = data?.payee;
  const [payeeDataForEdit, setPayeeDataForEdit] = useState<PayeeData | undefined>(undefined);

  // Helper function to display colored status
  const renderPayeeStatus = (status: PayeeStatusType) => {
    switch (status) {
      case PayeeStatus.Pending:
        return <Text style={{ color: Colors.Neutral800 }}>Pending review</Text>;
      case PayeeStatus.Approved:
        return <Text style={{ color: Colors.Success600 }}>Approved</Text>;
      case PayeeStatus.Rejected:
        return <Text style={{ color: Colors.Danger600 }}>Returned</Text>;
      default:
        return <Text style={{ color: Colors.Neutral800 }}>{status || "Unknown"}</Text>;
    }
  };

  useEffect(() => {
    if (payee) {
      void (async () => {
        const transformedData = await transformPayeeDataForEdit(payee);
        setPayeeDataForEdit(transformedData);
      })();
    }
  }, [payee]);
  const currentUser = data?.currentUser;
  const canRevealPaymentPref = Boolean(currentUser?.canRevealPaymentPrefPermission);

  const [approvePayee, { loading: approveLoading }] = useApprovePayeeMutation({
    context: clientContext(),
    refetchQueries: ["GetPayees"],
    onError: (err) => {
      toast.error(err.message, { position: "bottom-right" });
    },
    onCompleted: (result) => {
      if (result) {
        window.location.href = "/canopy/treasury/payees";
      }
    },
  });

  const [returnPayee, { loading: returnLoading }] = useReturnPayeeMutation({
    context: clientContext(),
    refetchQueries: ["GetPayees", "GetPayeeLogs"],
    onError: (err) => {
      toast.error(err.message, { position: "bottom-right" });
    },
    onCompleted: (result) => {
      if (result) {
        window.location.href = "/canopy/treasury/payees";
      }
    },
  });

  const handleApprove = useCallback(() => {
    if (!payee?.id) return;
    void approvePayee({
      variables: { payeeId: payee.id },
    });
  }, [approvePayee, payee?.id]);

  const handleReturn = useCallback(() => {
    if (!payee?.id) return;
    setShowReturnModal(true);
  }, [payee?.id]);

  const handleReturnModalClose = useCallback(() => {
    setShowReturnModal(false);
    setReturnReason("");
  }, []);

  const handleReturnSubmit = useCallback(() => {
    if (!payee?.id || !returnReason.trim()) return;

    void returnPayee({
      variables: { payeeId: payee.id, reason: returnReason.trim() },
    });

    handleReturnModalClose();
  }, [returnPayee, payee?.id, returnReason, handleReturnModalClose]);

  const isUserSameAsLastModified = Boolean(
    currentUser?.id && payee?.lastModifiedBy?.id === currentUser.id,
  );

  const showActionButtons = payee?.status === PayeeStatus.Pending;
  const shouldDisableButtons = isUserSameAsLastModified;
  const showEditButton =
    payee?.status === PayeeStatus.Approved || payee?.status === PayeeStatus.Rejected;

  const paymentPrefDocs = payee?.paymentPrefDocuments || [];
  const primaryDocument = paymentPrefDocs[0];
  const documentUrl = primaryDocument?.viewUrl || "";

  const payeeDocs = payee?.payeeDocuments || [];
  const primaryPayeeDocument = payeeDocs[0];

  const getTaxFormLabel = () => {
    if (!primaryPayeeDocument) return "";

    switch (primaryPayeeDocument.documentType) {
      case "us_tax":
        return "W9 (US vendor)";
      case "non_us_tax":
        return "W8 (Non-US vendor)";
      default:
        return "Tax document";
    }
  };

  const taxFormLabel = getTaxFormLabel();

  const hasPayeeDetailsToShow = Boolean(
    payee?.name ||
      (payee?.address &&
        [
          payee.address.street,
          payee.address.street2,
          payee.address.street3,
          payee.address.city,
          payee.address.state?.name || payee.address.region,
          payee.address.postalCode,
          payee.address.country?.name,
        ].filter(Boolean).length > 0) ||
      payee?.taxId ||
      taxFormLabel ||
      (primaryPayeeDocument && primaryPayeeDocument.viewUrl) ||
      (payee?.form1099Required !== null && payee?.form1099Required !== undefined),
  );

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const handleEditPayee = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleExit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const renderEditWorkflow = useCallback(() => {
    if (!isEditing && !payeeDataForEdit) return null;
    switch (payee?.payeeType) {
      case PayeeType.Vendor:
        // closes the slideoutdrawerv2 when starting the workflow
        onClose();
        return (
          <PayeeVendorWorkflow
            onExit={handleExit}
            payeeState={payeeDataForEdit}
            workflowState={{ stepNumber: 0, mode: PayeeWorkflowModes.EDIT }}
          />
        );
      case PayeeType.Entity:
        onClose();
        return (
          <PayeeEntityWorkflow
            onExit={handleExit}
            payeeState={payeeDataForEdit}
            workflowState={{ stepNumber: 0, mode: PayeeWorkflowModes.EDIT }}
          />
        );
      case PayeeType.Investment:
        onClose();
        return (
          <PayeeInvestmentWorkflow
            onExit={handleExit}
            payeeState={payeeDataForEdit}
            workflowState={{ stepNumber: 0, mode: PayeeWorkflowModes.EDIT }}
          />
        );
      default:
        return null;
    }
  }, [isEditing, handleExit]);

  return (
    <>
      <SlideoutDrawerV2
        // @ts-expect-error - OverlayStyle enum properly exported but auto-formatter removes import
        overlayStyle="transparent"
        isOpen={isOpen}
        onClose={onClose}
        data-testid="payee-details-slideout"
      >
        {loading ? (
          <Flex flexDirection="column" alignItems="center" justifyContent="center">
            <LoadingIndicator />
          </Flex>
        ) : error ? (
          <Flex flexDirection="column" alignItems="center" justifyContent="center">
            <Text variant="regular-md" color="error">
              Error loading payee details: {error.message}
            </Text>
          </Flex>
        ) : payee ? (
          <Flex className={slideoutContainer}>
            <Flex className={leftColumn}>
              {!primaryDocument ? (
                <Flex flexDirection="column" alignItems="center" justifyContent="center">
                  <Text variant="regular-md" color="muted">
                    No payment preference documents available for this payee.
                  </Text>
                </Flex>
              ) : (
                <DocumentTracker
                  documentName={primaryDocument.name}
                  documentUrl={documentUrl}
                  slideoutMode={true}
                />
              )}
            </Flex>
            <Flex className={rightColumn}>
              <Flex
                flexDirection="column"
                className={`${rightColumnHeader} ${complexHeaderOverride}`}
              >
                <SlideoutDrawerV2.ComplexHeader
                  title={payee.name}
                  subtitles={[
                    payee.payeeType === "vendor"
                      ? "Vendor"
                      : payee.payeeType === "entity"
                        ? "Entity"
                        : payee.payeeType === "investment"
                          ? "Investment"
                          : "Payee",
                    renderPayeeStatus(payee.status),
                  ]}
                />
                <Flex className={rightColumnTabsContainer}>
                  <Tabs>
                    <Tab
                      role="tab"
                      text={PayeeDetailsTabs.Overview}
                      onClick={() => handleTabChange(PayeeDetailsTabs.Overview)}
                      active={activeTab === PayeeDetailsTabs.Overview}
                    />
                    <Tab
                      role="tab"
                      text={PayeeDetailsTabs.Log}
                      onClick={() => handleTabChange(PayeeDetailsTabs.Log)}
                      active={activeTab === PayeeDetailsTabs.Log}
                    />
                  </Tabs>
                </Flex>
              </Flex>
              <Flex flexDirection="column" className={rightColumnContent} gap={16}>
                {activeTab === PayeeDetailsTabs.Overview && (
                  <>
                    {payee.arena?.name && (
                      <Card padding="lg" title="Client details">
                        <PaymentDetailField label="Client" value={payee.arena.name} />
                      </Card>
                    )}

                    {hasPayeeDetailsToShow && payee.payeeType === PayeeType.Vendor && (
                      <Card padding="lg" title="Payee details">
                        <Flex flexDirection="column" gap={16}>
                          {payee.name && (
                            <PaymentDetailField
                              label={
                                payee.payeeType === "vendor"
                                  ? "Vendor"
                                  : payee.payeeType === "entity"
                                    ? "Entity"
                                    : "Investment"
                              }
                              value={payee.name}
                            />
                          )}

                          {payee.address &&
                            [
                              payee.address.street,
                              payee.address.street2,
                              payee.address.street3,
                              payee.address.city,
                              payee.address.state?.name || payee.address.region,
                              payee.address.postalCode,
                              payee.address.country?.name,
                            ].filter(Boolean).length > 0 && (
                              <Flex>
                                <Flex flexBasis="25%">
                                  <Text variant="secondary-md">Address</Text>
                                </Flex>
                                <Flex flexBasis="75%" flexDirection="column">
                                  {[
                                    payee.address.street,
                                    payee.address.street2,
                                    payee.address.street3,
                                    payee.address.city,
                                    payee.address.state?.name || payee.address.region,
                                    payee.address.postalCode,
                                    payee.address.country?.name,
                                  ]
                                    .filter(Boolean)
                                    .map((line, index) => (
                                      <Text key={index} variant="regular-md">
                                        {line}
                                      </Text>
                                    ))}
                                </Flex>
                              </Flex>
                            )}

                          {payee.taxId && (
                            <PaymentDetailField label="Tax ID" value={payee.taxId} />
                          )}

                          {(taxFormLabel ||
                            (primaryPayeeDocument && primaryPayeeDocument.viewUrl)) && (
                            <Flex flexDirection="column">
                              {taxFormLabel && (
                                <Flex>
                                  <Flex flexBasis="25%">
                                    <Text variant="secondary-md">Tax form</Text>
                                  </Flex>
                                  <Flex flexBasis="75%">
                                    <Text variant="regular-md">{taxFormLabel}</Text>
                                  </Flex>
                                </Flex>
                              )}

                              {primaryPayeeDocument && primaryPayeeDocument.viewUrl && (
                                <Flex>
                                  <Flex flexBasis="25%"></Flex>
                                  <Flex flexBasis="75%">
                                    <Link
                                      href={primaryPayeeDocument.viewUrl}
                                      shouldOpenInNewTab
                                      align="left"
                                      icon="external-link"
                                      iconPosition="right"
                                    >
                                      {primaryPayeeDocument.name}
                                    </Link>
                                  </Flex>
                                </Flex>
                              )}
                            </Flex>
                          )}
                          {payee.form1099Required !== null &&
                            payee.form1099Required !== undefined && (
                              <PaymentDetailField
                                label="1099 Required"
                                value={payee.form1099Required ? "Yes" : "No"}
                              />
                            )}
                        </Flex>
                      </Card>
                    )}

                    {payee.paymentPref?.paymentPrefFull && (
                      <Card padding="lg" title="Payment instructions">
                        <PayeePaymentPrefDisplayCard
                          paymentPref={payee.paymentPref.paymentPrefFull}
                          displayRevealDetails={canRevealPaymentPref}
                        />
                      </Card>
                    )}
                  </>
                )}
                {activeTab === PayeeDetailsTabs.Log && (
                  <Flex flexDirection="column" gap={16}>
                    <PayeeLogsList
                      logs={logsData?.payee?.logs || []}
                      loading={logsLoading}
                      error={logsError}
                    />
                  </Flex>
                )}
              </Flex>
              {showActionButtons ? (
                <Flex
                  className={actionButtonsContainer}
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={12}
                >
                  {shouldDisableButtons ? (
                    <HelpIndicator
                      id="return-help"
                      content="You cannot review your own change."
                      placement="top"
                    >
                      <div>
                        <Button
                          variant="neutral"
                          onClick={handleReturn}
                          disabled={shouldDisableButtons || returnLoading}
                          isLoading={returnLoading}
                        >
                          {`Return ${payee.payeeType.toLowerCase()}`}
                        </Button>
                      </div>
                    </HelpIndicator>
                  ) : (
                    <Button
                      variant="neutral"
                      onClick={handleReturn}
                      disabled={shouldDisableButtons || returnLoading}
                      isLoading={returnLoading}
                    >
                      {`Return ${payee.payeeType.toLowerCase()}`}
                    </Button>
                  )}
                  {shouldDisableButtons ? (
                    <HelpIndicator
                      id="approve-help"
                      content="You cannot review your own change."
                      placement="top"
                    >
                      <div>
                        <Button
                          variant="primary"
                          onClick={handleApprove}
                          disabled={shouldDisableButtons || approveLoading}
                          isLoading={approveLoading}
                        >
                          {`Approve ${payee.payeeType.toLowerCase()}`}
                        </Button>
                      </div>
                    </HelpIndicator>
                  ) : (
                    <Button
                      variant="primary"
                      onClick={handleApprove}
                      disabled={shouldDisableButtons || approveLoading}
                      isLoading={approveLoading}
                    >
                      {`Approve ${payee.payeeType.toLowerCase()}`}
                    </Button>
                  )}
                </Flex>
              ) : (
                showEditButton && (
                  <Flex
                    className={actionButtonsContainer}
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={12}
                  >
                    <Button variant="neutral" onClick={handleEditPayee}>
                      Edit
                    </Button>

                    <Button variant="primary" onClick={handleEditPayee} disabled={true}>
                      Archive
                    </Button>
                  </Flex>
                )
              )}
            </Flex>
          </Flex>
        ) : (
          <div />
        )}
      </SlideoutDrawerV2>

      {/* Return Reason Modal */}
      <ModalV2
        show={showReturnModal}
        onHide={handleReturnModalClose}
        title="Return reason"
        aria-describedby="return-reason-description"
      >
        <Flex flexDirection="column" gap={16} className={returnReasonModalContent}>
          <div id="return-reason-description">
            <Textarea
              size="small"
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder={`Please add the reason for returning the ${payee?.payeeType.toLowerCase()}`}
              isFullWidth
            />
          </div>
          <Flex flexDirection="row" justifyContent="flex-end" gap={12}>
            <Button variant="neutral" onClick={handleReturnModalClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleReturnSubmit}
              disabled={!returnReason.trim() || returnLoading}
              isLoading={returnLoading}
            >
              Submit reason
            </Button>
          </Flex>
        </Flex>
      </ModalV2>
      {renderEditWorkflow()}
    </>
  );
};
//comment
export default PayeeDetails;
